/**
 * Pure refund-amount math, extracted from RefundCreateModal so it can be unit-tested
 * (it decides how much money is refunded). No React, no side effects — same logic as
 * before, just importable. See refundAmount.test.js.
 */

const round2 = (n) => Math.round(n * 100) / 100;

// Rate-based ESTIMATE of the THB paid for one order, split into selectable components.
// This recomputes from rate + fees and can round differently from what was actually
// charged, so the modal prefers the backend's exact `charged*` figures and uses this only
// as a fallback / side-by-side estimate. base = product (× qty) + JP delivery (in yen).
// Product paid depends on payment status; a "มัดจำ 50%" deposit also folds in the downRate
// fee, credit adds the creditRate %. Transaction fee (ค่ากด) is separate, counted only when paid.
export const computeComponents = (row) => {
  const qty = parseInt(row.qty || 1, 10) || 1;
  const base = parseFloat(row.amount || 0) * qty + parseFloat(row.deliveryFeeJp || 0);
  const productRate = parseFloat(row.productRate || 0);
  const downRate = parseFloat(row.downRate || 0);
  const creditRate = parseFloat(row.creditRate || 0);

  let product = 0;
  if (row.productPayment === 'ชำระครบแล้ว') {
    // full payment; a down order also paid the downRate fee on its deposit
    product = base * productRate;
    if (row.isDown) product += base * (downRate / 100) * productRate;
  } else if (row.productPayment === 'มัดจำ 50%') {
    product = (base * 0.5 + base * (downRate / 100)) * productRate;
  } // รอชำระ / คืนเงินแล้ว → 0

  if (row.isCredit) product *= (1 + creditRate / 100);

  // Fee component = paid transaction fee (ค่ากด) + paid extra JP fees (e.g. anshin).
  // Both refund together under the same includeFee toggle.
  const txnFee = row.transactionFeePaid ? parseFloat(row.transactionFee || 0) : 0;
  const extraFees = row.extraJpFeesPaid && Array.isArray(row.extraJpFees)
    ? row.extraJpFees.reduce((sum, f) => sum + (parseFloat(f?.amountThb) || 0), 0)
    : 0;
  const fee = txnFee + extraFees;
  // Round to 2 decimals (satang), not whole baht — refunds keep the exact amount.
  return { productAmt: round2(product), feeAmt: round2(fee) };
};

// The 3.5% card fee an OLD (non-platform) customer paid when they charged an order to a
// credit card via Beam. Refunding that payment adds it back so they get exactly what they
// paid. Platform customers pay orders from their wallet (card is top-up only), so their
// refund is plain cost — the lookup sets isCreditCard=false for them and this never applies.
export const CARD_FEE_RATE = 0.035;

// Contribution of a single item to the refund total, honoring the component toggles and —
// for an old-customer card order — the 3.5% card fee. productAmt / feeAmt are the exact
// charged figures (platform customers) or the rate estimate (old customers).
export const itemAmount = (l) => {
  let base;
  if (l.isManual) base = parseFloat(l.amount) || 0;
  else if (!l.orderId) base = 0;
  else base = (l.includeProduct ? (l.productAmt || 0) : 0) + (l.includeFee ? (l.feeAmt || 0) : 0);
  return l.isCreditCard ? base * (1 + CARD_FEE_RATE) : base;
};

// Sum every link's contribution, counting each order at most once — if the same order is
// on two link rows (e.g. the same URL pasted twice), it isn't double-counted. Manual rows
// have no orderId, so each is always counted.
export const sumRefundTotal = (links = []) => {
  const seenOrderIds = new Set();
  return links.reduce((sum, l) => {
    if (!l.isManual && l.orderId) {
      if (seenOrderIds.has(l.orderId)) return sum;
      seenOrderIds.add(l.orderId);
    }
    return sum + itemAmount(l);
  }, 0);
};
